import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-levi-bg pt-32 pb-20">
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid-red bg-[size:60px_60px] opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-levi-bg via-transparent to-levi-bg" />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-levi-primary/20 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 -left-32 w-[400px] h-[400px] bg-levi-accent/10 rounded-full blur-[100px]" />

      <div className="container-xl relative grid lg:grid-cols-12 gap-12 items-center">
        {/* Left - Copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-12 bg-levi-primary" />
            <span className="eyebrow">Independent Studio · Est. 2025</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="display-xl text-levi-text"
          >
            Crafted with
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-levi-primary via-red-500 to-levi-accent">
              Precision.
            </span>
            <br />
            Built to Endure.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-levi-muted leading-relaxed"
          >
            We design and ship mobile apps that cut through the noise — disciplined,
            focused, and relentlessly polished. No fluff. No shortcuts.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a href="#work" className="btn-primary group">
              View the Work
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#contact" className="btn-ghost group">
              <Play size={14} />
              Start a Project
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[
              { k: "12+", v: "Apps Shipped" },
              { k: "500K", v: "Users Reached" },
              { k: "4.8★", v: "Avg. Rating" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-3xl md:text-4xl text-levi-text">{s.k}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-levi-muted">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right - Logo/Portrait */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-md"
          >
            <div className="absolute -inset-8 bg-levi-primary/30 blur-3xl animate-pulse-glow" />
            <div className="relative aspect-square rounded-3xl overflow-hidden border border-levi-border shadow-card">
              <img
                src="/assets/levi-logo.jpg"
                alt="Phuc Pham"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-levi-bg/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-ultra uppercase text-levi-muted">Studio / Founder</div>
                  <div className="font-display text-xl tracking-widest text-white">Phuc Pham</div>
                </div>
                <div className="h-2 w-2 rounded-full bg-levi-primary animate-pulse" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom marker */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="font-mono text-[10px] uppercase tracking-ultra text-levi-muted">Scroll</div>
        <div className="h-10 w-px bg-gradient-to-b from-levi-primary to-transparent" />
      </div>
    </section>
  );
}

export default Hero;
